import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Key, Plus, Edit, Trash2, Download, X, Upload, Search,
  CheckCircle, AlertCircle, Clock, RotateCcw, ChevronDown, ChevronUp,
  Image as ImageIcon, FileText, User, Calendar, Loader2
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ---- Signature Pad Component ----
const SignaturePad = ({ onSave, onCancel, title }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
    setHasDrawn(false);
  };

  const saveSignature = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400">{title}</p>
      <div className="border border-gray-600 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair"
          style={{ height: '150px', touchAction: 'none' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex gap-2">
        <button onClick={clearCanvas} className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600">Tøm</button>
        <button onClick={saveSignature} disabled={!hasDrawn} className="px-3 py-1.5 bg-yellow-500 text-gray-900 rounded text-sm font-medium hover:bg-yellow-400 disabled:opacity-40">Lagre signatur</button>
        {onCancel && <button onClick={onCancel} className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded text-sm hover:bg-gray-600">Avbryt</button>}
      </div>
    </div>
  );
};

// ---- Image Lightbox ----
const ImageLightbox = ({ src, onClose }) => (
  <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300"><X size={32} /></button>
    <img src={src} alt="Nøkkelbilde" className="max-w-full max-h-full object-contain rounded-lg" />
  </div>
);

// ---- Main Component ----
const KeyAdmin = ({ toast }) => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [receipts, setReceipts] = useState([]);
  const [returnedReceipts, setReturnedReceipts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [showSignInDialog, setShowSignInDialog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // Form state
  const [form, setForm] = useState({
    member_id: '', member_name: '', date_issued: new Date().toISOString().split('T')[0],
    description: '', image_url: '', recipient_signature: '', uploaded_pdf_url: '',
    uploaded_filename: '', uploaded_file_type: ''
  });

  const fetchReceipts = useCallback(async () => {
    try {
      const [activeRes, returnedRes] = await Promise.all([
        axios.get(`${API_BASE}/api/club-operations/keys?status=active`, { headers }),
        axios.get(`${API_BASE}/api/club-operations/keys?status=returned`, { headers })
      ]);
      setReceipts(activeRes.data.receipts || []);
      setReturnedReceipts(returnedRes.data.receipts || []);
    } catch (err) { console.error('Error fetching receipts:', err); }
  }, []);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/club-operations/keys/audit-log`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (err) { console.error('Error fetching audit logs:', err); }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/users`, { headers });
      setMembers(res.data || []);
    } catch (err) { console.error('Error fetching members:', err); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchReceipts(), fetchMembers(), fetchAuditLogs()]);
      setLoading(false);
    };
    init();
  }, []);

  const resetForm = () => {
    setForm({ member_id: '', member_name: '', date_issued: new Date().toISOString().split('T')[0], description: '', image_url: '', recipient_signature: '', uploaded_pdf_url: '' });
    setEditingReceipt(null);
    setShowForm(false);
    setUploadMode(false);
  };

  const handleMemberSelect = (e) => {
    const memberId = e.target.value;
    const member = members.find(m => m.id === memberId);
    setForm(f => ({ ...f, member_id: memberId, member_name: member ? member.full_name : '' }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Kun bildefiler er tillatt'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/api/club-operations/keys/upload-image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({ ...f, image_url: res.data.image_url }));
      toast.success('Bilde lastet opp');
    } catch (err) {
      toast.error('Kunne ikke laste opp bilde');
    } finally { setUploading(false); }
  };

  const handleFileInput = (e) => {
    handleImageUpload(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const handleDocUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg',
      'application/vnd.oasis.opendocument.text', 'application/vnd.oasis.opendocument.spreadsheet',
      'application/vnd.oasis.opendocument.presentation'];
    const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png', '.odt', '.ods', '.odp'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowed.includes(file.type) && !allowedExts.includes(ext)) {
      toast.error('Tillatte formater: PDF, JPG, PNG, ODT, ODS');
      return;
    }
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/api/club-operations/keys/upload-document`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({
        ...f,
        uploaded_pdf_url: res.data.doc_url,
        uploaded_filename: res.data.filename,
        uploaded_file_type: res.data.file_type
      }));
      toast.success('Fil lastet opp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke laste opp fil');
    } finally { setUploadingPdf(false); }
  };

  const handleDocFileInput = (e) => { handleDocUpload(e.target.files[0]); };

  const handleSave = async () => {
    if (!form.member_name) { toast.error('Velg et medlem'); return; }
    if (!form.date_issued) { toast.error('Velg dato utlevert'); return; }
    if (!uploadMode && !form.description.trim()) { toast.error('Beskrivelse er påkrevd'); return; }
    if (uploadMode && !form.uploaded_pdf_url) { toast.error('Last opp en PDF-fil'); return; }
    setSaving(true);
    try {
      if (editingReceipt) {
        await axios.put(`${API_BASE}/api/club-operations/keys/${editingReceipt.id}`, form, { headers });
        toast.success('Nøkkelkvittering oppdatert');
      } else {
        await axios.post(`${API_BASE}/api/club-operations/keys`, form, { headers });
        toast.success('Nøkkelkvittering opprettet');
      }
      resetForm();
      await Promise.all([fetchReceipts(), fetchAuditLogs()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke lagre');
    } finally { setSaving(false); }
  };

  const handleEdit = (receipt) => {
    setForm({
      member_id: receipt.member_id || '',
      member_name: receipt.member_name || '',
      date_issued: receipt.date_issued || '',
      description: receipt.description || '',
      image_url: receipt.image_url || '',
      recipient_signature: receipt.recipient_signature || '',
      uploaded_pdf_url: receipt.uploaded_pdf_url || '',
      uploaded_filename: receipt.uploaded_filename || '',
      uploaded_file_type: receipt.uploaded_file_type || ''
    });
    setUploadMode(!!receipt.uploaded_pdf_url);
    setEditingReceipt(receipt);
    setShowForm(true);
  };

  const handleDelete = async (receipt) => {
    try {
      await axios.delete(`${API_BASE}/api/club-operations/keys/${receipt.id}`, { headers });
      toast.success('Nøkkelkvittering slettet');
      setShowDeleteConfirm(null);
      await Promise.all([fetchReceipts(), fetchAuditLogs()]);
    } catch (err) { toast.error('Kunne ikke slette'); }
  };

  const handleRestore = async (keyId) => {
    try {
      await axios.post(`${API_BASE}/api/club-operations/keys/${keyId}/restore`, {}, { headers });
      toast.success('Nøkkelkvittering gjenopprettet');
      await Promise.all([fetchReceipts(), fetchAuditLogs()]);
    } catch (err) { toast.error('Kunne ikke gjenopprette'); }
  };

  const handleSignIn = async (keyId, signature, dateReturned, allKeysVerified, returnComment) => {
    try {
      await axios.post(`${API_BASE}/api/club-operations/keys/${keyId}/sign-in`, {
        leader_signature: signature,
        date_returned: dateReturned,
        all_keys_verified: allKeysVerified,
        return_comment: returnComment || ''
      }, { headers });
      toast.success('Nøkler innlevert og signert');
      setShowSignInDialog(null);
      setSubTab('returned'); // Auto-switch to Innleverte tab after successful return
      await Promise.all([fetchReceipts(), fetchAuditLogs()]);
    } catch (err) { toast.error('Kunne ikke signere innlevering'); }
  };

  const handleDownloadPdf = async (receipt) => {
    if (receipt.uploaded_pdf_url) {
      const a = document.createElement('a');
      a.href = receipt.uploaded_pdf_url;
      a.download = receipt.uploaded_filename || 'nokkelkvittering';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/api/club-operations/keys/${receipt.id}/pdf`, {
        headers, responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Nokkelkvittering_${receipt.member_name.replace(/\s/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Kunne ikke laste ned PDF'); }
  };

  const getStatusInfo = (receipt) => {
    if (receipt.status === 'returned') return { icon: <CheckCircle size={16} />, color: 'text-green-400', dot: 'bg-green-500', label: 'Innlevert' };
    if (receipt.uploaded_pdf_url) return { icon: <FileText size={16} />, color: 'text-blue-400', dot: 'bg-blue-500', label: 'Opplastet fil' };
    if (!receipt.recipient_signature) return { icon: <AlertCircle size={16} />, color: 'text-red-400', dot: 'bg-red-500', label: 'Ikke signert' };
    return { icon: <CheckCircle size={16} />, color: 'text-green-400', dot: 'bg-green-500', label: 'Aktiv - Signert' };
  };

  const filteredReceipts = (subTab === 'active' ? receipts : returnedReceipts).filter(r =>
    !searchTerm || r.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) || r.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  return (
    <div className="space-y-4" data-testid="key-admin-section">
      {lightboxImage && <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />}

      {/* Sub-tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <button onClick={() => setSubTab('active')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === 'active' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-testid="keyadmin-tab-active">
          <Key className="w-4 h-4 inline mr-1" /> Utleverte nøkler ({receipts.length})
        </button>
        <button onClick={() => setSubTab('returned')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${subTab === 'returned' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-testid="keyadmin-tab-returned">
          <CheckCircle className="w-4 h-4 inline mr-1" /> Innleverte nøkler ({returnedReceipts.length})
        </button>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="text" placeholder="Søk..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white w-48" data-testid="keyadmin-search" />
          </div>
          {subTab === 'active' && (
            <button onClick={() => { resetForm(); setShowForm(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg text-sm font-medium hover:bg-yellow-400" data-testid="keyadmin-add-btn">
              <Plus size={16} /> Ny utlevering
            </button>
          )}
        </div>
      </div>

      {/* Receipt List */}
      <Card className="bg-gray-900 border-gray-800">
        <CardContent className="p-0">
          {filteredReceipts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{subTab === 'active' ? 'Ingen aktive nøkkelutleveringer' : 'Ingen innleverte nøkler'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {filteredReceipts.map(receipt => {
                const statusInfo = getStatusInfo(receipt);
                const isActive = subTab === 'active' && receipt.status !== 'returned';
                const handleRowClick = () => {
                  if (isActive) {
                    setShowSignInDialog(receipt);
                  } else {
                    handleDownloadPdf(receipt);
                  }
                };
                return (
                  <div
                    key={receipt.id}
                    onClick={handleRowClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); } }}
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-3 p-3 hover:bg-gray-800/60 focus:bg-gray-800/60 outline-none transition-colors cursor-pointer"
                    data-testid={`key-receipt-${receipt.id}`}
                    title={isActive ? 'Klikk for å registrere innlevering' : 'Klikk for å laste ned kvittering'}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusInfo.dot}`} title={statusInfo.label} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm truncate">{receipt.member_name}</span>
                        <span className={`text-xs ${statusInfo.color} flex items-center gap-1`}>{statusInfo.icon}{statusInfo.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5 flex-wrap">
                        <span><Calendar size={11} className="inline mr-1" />Utlevert: {receipt.date_issued || '-'}</span>
                        {receipt.date_returned && <span>Innlevert: {receipt.date_returned}</span>}
                        {receipt.received_by_name && subTab === 'returned' && (
                          <span className="text-green-400"><User size={11} className="inline mr-1" />Mottatt av: {receipt.received_by_name}</span>
                        )}
                        {receipt.all_keys_verified && subTab === 'returned' && (
                          <span className="text-green-400"><CheckCircle size={11} className="inline mr-1" />Alle nøkler verifisert</span>
                        )}
                        {receipt.created_by_name && <span><User size={11} className="inline mr-1" />Utstedt av: {receipt.created_by_name}</span>}
                        {receipt.description && <span className="truncate max-w-[200px]">{receipt.description.split('\n')[0]}</span>}
                      </div>
                      {/* Return comment (mangler/spesialavtaler) - vises kun i Innleverte-tab */}
                      {subTab === 'returned' && receipt.return_comment && (
                        <div className="mt-1.5 text-xs text-yellow-200 bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-1 whitespace-pre-wrap" data-testid={`key-return-comment-${receipt.id}`}>
                          <span className="font-semibold text-yellow-300">Kommentar:</span> {receipt.return_comment}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {isActive && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowSignInDialog(receipt); }}
                          className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-md text-xs font-medium"
                          title="Registrer innlevering — dato + mottaker fylles inn automatisk"
                          data-testid={`key-return-toggle-${receipt.id}`}
                        >
                          <RotateCcw size={13} />
                          <span className="hidden sm:inline">Lever inn</span>
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleDownloadPdf(receipt); }} className="p-1.5 text-gray-400 hover:text-white rounded" title="Last ned PDF" data-testid={`key-pdf-${receipt.id}`}><Download size={16} /></button>
                      {subTab === 'active' && (
                        <>
                          <button onClick={() => handleEdit(receipt)} className="p-1.5 text-gray-400 hover:text-white rounded" title="Rediger" data-testid={`key-edit-${receipt.id}`}><Edit size={16} /></button>
                          <button onClick={() => setShowDeleteConfirm(receipt)} className="p-1.5 text-red-400 hover:text-red-300 rounded" title="Slett" data-testid={`key-delete-${receipt.id}`}><Trash2 size={16} /></button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Log Section */}
      <div className="mt-6">
        <button onClick={() => { setShowAuditLog(!showAuditLog); if (!showAuditLog) fetchAuditLogs(); }}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-2" data-testid="keyadmin-auditlog-toggle">
          {showAuditLog ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          <Clock size={16} /> Endringslogg ({auditLogs.length})
        </button>
        {showAuditLog && (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-0 max-h-80 overflow-y-auto">
              {auditLogs.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">Ingen loggoppføringer ennå</div>
              ) : (
                <div className="divide-y divide-gray-800">
                  {auditLogs.map(log => (
                    <div key={log.id} className="p-3 text-sm" data-testid={`audit-log-${log.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-white">{log.details}</span>
                        <div className="flex items-center gap-2">
                          {log.action === 'deleted' && (
                            <button onClick={() => handleRestore(log.key_receipt_id)}
                              className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30" data-testid={`audit-restore-${log.id}`}>
                              Gjenopprett
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-3 text-xs text-gray-500 mt-1">
                        <span>{log.performed_by_name}</span>
                        <span>{new Date(log.timestamp).toLocaleString('nb-NO')}</span>
                        <span className={`capitalize ${log.action === 'deleted' ? 'text-red-400' : log.action === 'restored' ? 'text-blue-400' : log.action === 'created' ? 'text-green-400' : 'text-gray-400'}`}>
                          {log.action === 'created' ? 'Opprettet' : log.action === 'updated' ? 'Endret' : log.action === 'deleted' ? 'Slettet' : log.action === 'restored' ? 'Gjenopprettet' : log.action === 'signed_in' ? 'Innlevert' : log.action}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ---- CREATE/EDIT DIALOG ---- */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editingReceipt ? 'Rediger nøkkelkvittering' : 'Ny nøkkelutlevering'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {uploadMode ? 'Last opp en eksisterende nøkkelkvittering.' : 'Fyll ut feltene under for å registrere utlevering av nøkler/kort/brikker.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Mode toggle - only for new receipts */}
            {!editingReceipt && (
              <div className="flex gap-2 p-1 bg-gray-800 rounded-lg" data-testid="key-mode-toggle">
                <button onClick={() => setUploadMode(false)}
                  className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${!uploadMode ? 'bg-yellow-500 text-gray-900' : 'text-gray-400 hover:text-white'}`}>
                  Ny kvittering
                </button>
                <button onClick={() => setUploadMode(true)}
                  className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${uploadMode ? 'bg-yellow-500 text-gray-900' : 'text-gray-400 hover:text-white'}`}>
                  Last opp eksisterende
                </button>
              </div>
            )}

            {/* Member dropdown */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Nøkkelmottaker</label>
              <select value={form.member_id} onChange={handleMemberSelect}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="key-member-select">
                <option value="">Velg medlem...</option>
                {members.filter(m => m.approved && m.active !== false).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb')).map(m => (
                  <option key={m.id} value={m.id}>{m.full_name}</option>
                ))}
              </select>
              {form.member_name && <p className="text-xs text-gray-500 mt-1">Valgt: {form.member_name}</p>}
            </div>

            {/* Upload existing PDF mode */}
            {uploadMode && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Eksisterende nøkkelkvittering</label>
                {form.uploaded_pdf_url ? (
                  <div className="flex items-center gap-3 p-3 bg-gray-800 border border-gray-700 rounded-lg">
                    {['jpg','jpeg','png'].includes(form.uploaded_file_type) ? (
                      <img src={form.uploaded_pdf_url} alt="Opplastet" className="w-12 h-12 object-cover rounded cursor-pointer" onClick={() => setLightboxImage(form.uploaded_pdf_url)} />
                    ) : (
                      <FileText className="w-8 h-8 text-blue-400 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{form.uploaded_filename || 'Fil lastet opp'}</p>
                      <a href={form.uploaded_pdf_url} target="_blank" rel="noopener noreferrer" download className="text-xs text-blue-400 hover:underline">Last ned fil</a>
                    </div>
                    <button onClick={() => setForm(f => ({ ...f, uploaded_pdf_url: '', uploaded_filename: '', uploaded_file_type: '' }))}
                      className="p-1 bg-red-600 rounded-full text-white hover:bg-red-500 flex-shrink-0"><X size={14} /></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-28 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-gray-500" data-testid="key-pdf-upload">
                    {uploadingPdf ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : (
                      <>
                        <Upload className="w-6 h-6 text-gray-500 mb-1" />
                        <span className="text-sm text-gray-500">Klikk for å laste opp fil</span>
                        <span className="text-xs text-gray-600 mt-1">PDF, JPG, PNG, ODT, ODS</span>
                      </>
                    )}
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.odt,.ods,.odp" onChange={handleDocFileInput} className="hidden" />
                  </label>
                )}
              </div>
            )}

            {/* Image upload - show in both modes */}
            {!uploadMode && (
              <div>
                <label className="block text-sm text-gray-400 mb-1">Bilde av nøkler/kort/brikker (samlebilde)</label>
                <p className="text-xs text-gray-500 mb-2">Ta et samlebilde av alle nøkler/nøkkelkort/brikker som bildedokumentasjon.</p>
              {form.image_url ? (
                <div className="relative">
                  <img src={form.image_url} alt="Nøkkelbilde" className="w-full rounded-lg cursor-pointer border border-gray-700 max-h-48 object-cover"
                    onClick={() => setLightboxImage(form.image_url)} data-testid="key-image-preview" />
                  <button onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                    className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white hover:bg-red-500"><X size={14} /></button>
                </div>
              ) : (
                <label
                  className={`flex flex-col items-center justify-center w-full h-28 bg-gray-800 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${dragOver ? 'border-yellow-400 bg-yellow-500/10' : 'border-gray-700 hover:border-gray-500'}`}
                  onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
                  data-testid="key-image-upload">
                  {uploading ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> : (
                    <>
                      <Upload className="w-6 h-6 text-gray-500 mb-1" />
                      <span className="text-sm text-gray-500">{dragOver ? 'Slipp bildet her' : 'Dra og slipp bilde her, eller klikk for å velge'}</span>
                    </>
                  )}
                  <input type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
                </label>
              )}
            </div>
            )}

            {/* Date issued */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Dato utlevert</label>
              <input type="date" value={form.date_issued} onChange={e => setForm(f => ({ ...f, date_issued: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="key-date-issued" />
            </div>

            {/* Description - only required in normal mode */}
            {!uploadMode && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Beskrivelse</label>
              <p className="text-xs text-gray-500 mb-1">Skriv inn nøyaktig hva som er utlevert med nummer på nøkler, adgangskort, adgangsbrikker og annen identifikasjon.</p>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={4} placeholder="1. Nøkkelkort nr. 123&#10;2. Systemnøkkel K2 til Våganhallen"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-y" data-testid="key-description" />
              <p className="text-xs text-gray-500 italic mt-1">Tap av nøkler/kort/brikker koster penger og må dekkes av nøkkelinnehaver. Tap må meldes umiddelbart til klubbleder.</p>
            </div>
            )}

            {/* Signature - only in normal mode */}
            {!uploadMode && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Signatur av nøkkelmottaker</label>
              {form.recipient_signature ? (
                <div className="relative border border-gray-700 rounded-lg p-2 bg-white">
                  <img src={form.recipient_signature} alt="Signatur" className="h-20 mx-auto" data-testid="key-signature-preview" />
                  <button onClick={() => setForm(f => ({ ...f, recipient_signature: '' }))}
                    className="absolute top-1 right-1 p-1 bg-red-600 rounded-full text-white hover:bg-red-500"><X size={12} /></button>
                </div>
              ) : (
                <SignaturePad title="Tegn signatur med mus eller finger" onSave={(sig) => setForm(f => ({ ...f, recipient_signature: sig }))} />
              )}
            </div>
            )}
          </div>

          <DialogFooter className="mt-4">
            <button onClick={resetForm} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 text-sm flex items-center gap-2 disabled:opacity-50" data-testid="key-save-btn">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editingReceipt ? 'Oppdater' : 'Opprett kvittering'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---- SIGN-IN (RETURN) DIALOG ---- */}
      <SignInDialog receipt={showSignInDialog} onClose={() => setShowSignInDialog(null)} onSign={handleSignIn} />

      {/* ---- DELETE CONFIRM ---- */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Slett nøkkelkvittering</DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette kvitteringen for <strong>{showDeleteConfirm?.member_name}</strong>? Denne kan gjenopprettes fra endringsloggen.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
            <button onClick={() => handleDelete(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500 text-sm flex items-center gap-2" data-testid="key-delete-confirm-btn">
              <Trash2 size={14} /> Slett
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Sign-In Sub-Dialog ----
const SignInDialog = ({ receipt, onClose, onSign }) => {
  const [dateReturned, setDateReturned] = useState(new Date().toISOString().split('T')[0]);
  const [signature, setSignature] = useState(null);
  const [allKeysVerified, setAllKeysVerified] = useState(false);
  const [returnComment, setReturnComment] = useState('');

  if (!receipt) return null;

  return (
    <Dialog open={!!receipt} onOpenChange={() => onClose()}>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white">Innlevering av nøkler</DialogTitle>
          <DialogDescription className="text-gray-400">
            Registrer innlevering for <strong>{receipt.member_name}</strong>. Leder/styremedlem må signere.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Show original issue details so receiver knows what to look for */}
          <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-3 text-xs">
            <div className="text-gray-400 mb-1">Opprinnelig utlevering:</div>
            <div className="text-gray-200 whitespace-pre-wrap">{receipt.description || '(ingen beskrivelse)'}</div>
            {receipt.image_url && (
              <img src={receipt.image_url} alt="Utleverte nøkler" className="mt-2 max-h-32 rounded border border-gray-700" />
            )}
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Dato innlevert</label>
            <input type="date" value={dateReturned} onChange={e => setDateReturned(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="key-return-date" />
          </div>

          {/* Comment / notes field — for missing items or special agreements */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              Kommentar <span className="text-gray-500 font-normal">(valgfritt – noter mangler eller spesialavtaler)</span>
            </label>
            <textarea
              value={returnComment}
              onChange={e => setReturnComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="F.eks. 'Mangler garasjebrikke – låner ut denne i 2 uker til.' eller 'Kort nr. 3 er skadet.'"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 resize-none focus:outline-none focus:ring-2 focus:ring-yellow-500/40"
              data-testid="key-return-comment"
            />
            <div className="text-[10px] text-gray-500 mt-1 text-right">{returnComment.length}/1000</div>
          </div>

          {/* Verification checkbox — REQUIRED */}
          <label className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/40 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={allKeysVerified}
              onChange={e => setAllKeysVerified(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-yellow-500"
              data-testid="key-verify-all-keys"
            />
            <span className="text-xs text-yellow-100">
              <strong>Jeg bekrefter</strong> at jeg har mottatt <strong>alle</strong> nøkler/kort/brikker som ble utlevert (jf. beskrivelsen over).
            </span>
          </label>

          <div>
            {signature ? (
              <div className="relative border border-gray-700 rounded-lg p-2 bg-white">
                <img src={signature} alt="Leder-signatur" className="h-20 mx-auto" />
                <button onClick={() => setSignature(null)} className="absolute top-1 right-1 p-1 bg-red-600 rounded-full text-white hover:bg-red-500"><X size={12} /></button>
              </div>
            ) : (
              <SignaturePad title="Signatur nøkkelmottaker (leder / styremedlem)" onSave={setSignature} />
            )}
          </div>
        </div>
        <DialogFooter className="mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
          <button
            onClick={() => { if (signature && allKeysVerified) onSign(receipt.id, signature, dateReturned, allKeysVerified, returnComment.trim()); }}
            disabled={!signature || !allKeysVerified}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="key-signin-confirm-btn"
            title={!allKeysVerified ? 'Bekreft at alle nøkler er mottatt' : (!signature ? 'Signatur mangler' : '')}
          >
            Bekreft innlevering
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KeyAdmin;
